/**
 * Punktdaten aus einem anderen Punkt übernehmen: WAS übernommen werden kann.
 *
 * Eine Liste für beide Wege (Nutzer-Auftrag 28.09.2026, PLAN-punktdaten-uebernehmen.md):
 * - Weg 1: neuer Punkt aus einem offenen Protokoll (Anlege-Fenster, Haken je Abschnitt),
 * - Weg 2: in ein vorhandenes Protokoll aus einem anderen Punkt holen.
 *
 * **Positiv-Liste.** Übernommen wird nur, was hier steht. Ein neues Feld im Datenmodell
 * kommt also nie still mit, sondern erst, wenn es hier eingetragen wird. Bewusst NICHT
 * enthalten ist alles, was zum Ort oder zur Aufnahme selbst gehört: Nummer, Datum,
 * Kartierer, Labornummer, Bemerkungen, Projekt, Koordinaten, Forstamt, Revier,
 * Forstbetrieb, Forst- und Katasteradresse, Wuchsgebiet, Klimastufe, Witterung, Status,
 * Prüfhinweise, Gruppen, Änderungsvermerke und die KA-Aufnahme. Die Adresse holt die App
 * am neuen Ort selbst - und nur, wenn das Feld leer ist.
 *
 * Ohne Angular, damit sich die Regel allein prüfen lässt (`punkt-abschnitte.spec.ts`).
 */

import { cloneDeep } from 'lodash';
import { frischeZeilenKennungen, idOf, punktTemplate } from '../../data-model';

export type AbschnittId =
	| 'standort'
	| 'bestand'
	| 'grube'
	| 'bodenform'
	| 'messwerte'
	| 'substrat'
	| 'horizonte'
	| 'humus'
	| 'vegetation';

/** Die Reiter des Protokolls, nach denen die Abschnitte gruppiert werden. */
export type AbschnittReiter = 'Allgemein' | 'Boden' | 'Vegetation';

export interface AbschnittFeld {
	/** Pfad im Datensatz, mit Punkten getrennt. */
	pfad: string;
	/** Name wie in der Oberfläche. */
	name: string;
	/**
	 * Wird mit übernommen, zählt aber nicht als Angabe: Die App rechnet den Wert aus
	 * anderen Feldern und legt ihn mit einer Vorgabe an (nWSK = 0). Zählte er mit,
	 * hätte jeder Punkt eine „belegte" Substratfolge.
	 */
	abgeleitet?: boolean;
}

export interface Abschnitt {
	id: AbschnittId;
	titel: string;
	reiter: AbschnittReiter;
	felder: readonly AbschnittFeld[];
	/** Nur bei Gruben mit Bodenanalyse (Punktart 2 und 4) - nur dort stehen die Felder. */
	nurGrube?: boolean;
	/** Zeilenlisten, die eigene Kennungen bekommen (siehe `neueZeilenKennung`). */
	zeilen?: readonly string[];
}

export const ABSCHNITTE: readonly Abschnitt[] = [
	{
		id: 'standort', titel: 'Standort', reiter: 'Allgemein',
		felder: [
			{ pfad: 'extendedHeader.geologie', name: 'Geologie' },
			{ pfad: 'extendedHeader.morphologie', name: 'Morphologie' },
			{ pfad: 'extendedHeader.relief', name: 'Relief' },
			{ pfad: 'extendedHeader.mesoklima', name: 'Mesoklima' },
			{ pfad: 'extendedHeader.moorTyp', name: 'Moortyp' },
			{ pfad: 'extendedHeader.kMgCaPSerie', name: 'KMgCaP' },
			{ pfad: 'extendedHeader.hangrichtung', name: 'Hangrichtung' },
			{ pfad: 'extendedHeader.hangneigung', name: 'Hangneigung' },
		],
	},
	{
		id: 'bestand', titel: 'Bestand', reiter: 'Allgemein',
		felder: [
			{ pfad: 'extendedHeader.baumarten', name: 'Bestand' },
			{ pfad: 'extendedHeader.bestand', name: 'Bestand (aufbereitet)', abgeleitet: true },
			{ pfad: 'extendedHeader.bestandFreitext', name: 'Bestand (Freitext)' },
			{ pfad: 'extendedHeader.bestandBestaetigt', name: 'Bestand (bestätigt)', abgeleitet: true },
			{ pfad: 'extendedHeader.bestockungsTyp', name: 'Bestockungstyp' },
			{ pfad: 'extendedHeader.begruendungsart', name: 'Begründungsart' },
			{ pfad: 'extendedHeader.mischform', name: 'Mischform' },
			{ pfad: 'extendedHeader.waldfunktion', name: 'Waldfunktion' },
		],
	},
	{
		id: 'grube', titel: 'Grube: weitere Angaben', reiter: 'Allgemein', nurGrube: true,
		felder: [
			{ pfad: 'extendedHeader.klimabereich', name: 'Klimabereich' },
			{ pfad: 'extendedHeader.meereshoehe', name: 'Meereshöhe' },
			{ pfad: 'extendedHeader.grundgesteinSediment', name: 'Grundgestein/Sediment' },
			{ pfad: 'extendedHeader.lage', name: 'Lage' },
			{ pfad: 'extendedHeader.mosaikBereich', name: 'Mosaikbereich' },
			{ pfad: 'extendedHeader.mesorelief', name: 'Mesorelief' },
			{ pfad: 'extendedHeader.standortklimatischeAngaben', name: 'Standortklimatische Angaben' },
			{ pfad: 'extendedHeader.vorbestandlicheGeschichte', name: 'Vorbestandliche Geschichte' },
			{ pfad: 'extendedHeader.forstlicheBodenbearbeitung', name: 'Forstliche Bodenbearbeitung' },
			{ pfad: 'extendedHeader.anthropogeneBeeinflussung', name: 'Anthropogene Beeinflussung' },
			{ pfad: 'extendedHeader.duengemittelArt', name: 'Düngemittel' },
			{ pfad: 'extendedHeader.herbizide', name: 'Herbizide' },
			{ pfad: 'extendedHeader.insektizide', name: 'Insektizide' },
			{ pfad: 'extendedHeader.grundStauwasserVeraenderungen', name: 'Grund-/Stauwasserveränderungen' },
		],
	},
	{
		id: 'bodenform', titel: 'Bodenform und Wasserhaushalt', reiter: 'Boden',
		felder: [
			{ pfad: 'header.bodenform', name: 'Bodenform' },
			{ pfad: 'header.wasserstufe', name: 'Wasserform' },
			{ pfad: 'header.wasserstufe2', name: 'Wasserform' },
			{ pfad: 'header.substratFeuchte', name: 'Substratfeuchte' },
			{ pfad: 'extendedHeader.stfg', name: 'STFG' },
			{ pfad: 'extendedHeader.entwaesserungsverhalten', name: 'Entwässerungsverhalten' },
			{ pfad: 'extendedHeader.entwaesserungVorschlag', name: 'Entwässerungsverhalten', abgeleitet: true },
			{ pfad: 'extendedHeader.saugsaum', name: 'Saugsaum' },
			{ pfad: 'extendedHeader.spiegelTiefe', name: 'Spiegeltiefe' },
		],
	},
	{
		id: 'messwerte', titel: 'Messwerte (pH, V-Wert)', reiter: 'Boden',
		felder: [
			{ pfad: 'extendedHeader.ph', name: 'pH' },
			{ pfad: 'extendedHeader.vWertSaugsaum', name: 'V-Wert' },
			{ pfad: 'extendedHeader.vWertSaugsaumVermutet', name: 'V-Wert' },
		],
	},
	{
		id: 'substrat', titel: 'Substratfolge', reiter: 'Boden',
		zeilen: ['Substratfolge'],
		felder: [
			{ pfad: 'Substratfolge', name: 'Substratfolge' },
			{ pfad: 'extendedHeader.nWsk', name: 'nWSK', abgeleitet: true },
			{ pfad: 'extendedHeader.nWskStufe', name: 'nWSK-Stufe' },
			{ pfad: 'extendedHeader.karbonat', name: 'Karbonattiefe' },
			{ pfad: 'header.moorMaechtigkeit', name: 'Moormächtigkeit' },
		],
	},
	{
		id: 'horizonte', titel: 'Horizontfolge', reiter: 'Boden',
		zeilen: ['Horizontfolge'],
		felder: [
			{ pfad: 'Horizontfolge', name: 'Horizontfolge' },
			{ pfad: 'extendedHeader.gHorizont', name: 'Go/Gr/G' },
			{ pfad: 'extendedHeader.sHorizont', name: 'Sw/Sd/S' },
		],
	},
	{
		id: 'humus', titel: 'Humus', reiter: 'Boden',
		felder: [
			{ pfad: 'extendedHeader.humusAuflage', name: 'Humusauflage' },
			{ pfad: 'header.humusform', name: 'Humusform' },
		],
	},
	{
		id: 'vegetation', titel: 'Vegetation', reiter: 'Vegetation',
		zeilen: ['bodenvegetation.aufnahme.vegetationen'],
		felder: [
			{ pfad: 'bodenvegetation', name: 'Bodenvegetation' },
			{ pfad: 'header.vegetationsformengruppe', name: 'Vegetationsformengruppe' },
		],
	},
];

export const ABSCHNITT_REITER: readonly AbschnittReiter[] = ['Allgemein', 'Boden', 'Vegetation'];

/** Grube mit Bodenanalyse (2 Weisergrube, 4 Bodengrube) - wie `isG` im Datenmodell. */
function istGrube(punktart: string | undefined): boolean {
	const id = idOf(punktart ?? '');
	return id === 2 || id === 4;
}

/** Die Abschnitte, die es bei einem Punkt dieser Punktart gibt. */
export function abschnitteFuer(punktart: string | undefined): Abschnitt[] {
	return ABSCHNITTE.filter(a => !a.nurGrube || istGrube(punktart));
}

export function abschnitt(id: AbschnittId): Abschnitt {
	const a = ABSCHNITTE.find(x => x.id === id);
	if (!a) throw new Error(`Unbekannter Abschnitt: ${id}`);
	return a;
}

// ---------- Werte lesen und schreiben ----------

export function leseWert(punkt: any, pfad: string): any {
	let ort = punkt;
	for (const teil of pfad.split('.')) {
		if (ort === null || ort === undefined) return undefined;
		ort = ort[teil];
	}
	return ort;
}

function setzeWert(punkt: any, pfad: string, wert: any): void {
	const teile = pfad.split('.');
	let ort = punkt;
	for (const teil of teile.slice(0, -1)) {
		if (ort[teil] === null || typeof ort[teil] !== 'object') ort[teil] = {};
		ort = ort[teil];
	}
	const letzter = teile[teile.length - 1];
	if (wert === undefined) delete ort[letzter];
	else ort[letzter] = wert;
}

/**
 * Trägt der Wert eine Angabe? Kein Text, keine Zahl, kein gesetzter Haken, keine Zeile
 * heißt leer. Ein leeres Gerüst (`{ oben: '', trenner: undefined }`, `['', '']`) ist leer.
 * `false` ist leer: Ein nicht gesetzter Haken sagt nichts.
 */
export function hatInhalt(wert: unknown): boolean {
	if (wert === null || wert === undefined) return false;
	if (typeof wert === 'string') return wert.trim() !== '';
	if (typeof wert === 'number') return Number.isFinite(wert);
	if (typeof wert === 'boolean') return wert;
	if (Array.isArray(wert)) return wert.some(hatInhalt);
	if (wert instanceof Map) return wert.size > 0;
	if (typeof wert === 'object') return Object.values(wert as object).some(hatInhalt);
	return false;
}

/** Hat der Punkt in diesem Abschnitt irgendeine Angabe (ohne abgeleitete Werte)? */
export function hatAbschnittInhalt(punkt: any, id: AbschnittId): boolean {
	return abschnitt(id).felder.some(f => !f.abgeleitet && hatInhalt(leseWert(punkt, f.pfad)));
}

// ---------- Kurzfassung für die Vorschau ----------

function zeilenAnzahl(punkt: any, pfad: string): number {
	const liste = leseWert(punkt, pfad);
	return Array.isArray(liste) ? liste.filter(hatInhalt).length : 0;
}

function bodenformText(punkt: any): string {
	const bf = leseWert(punkt, 'header.bodenform.bf');
	if (!Array.isArray(bf)) return '';
	return bf
		.map((glied: any, i: number) => {
			const text = [glied?.prefix, glied?.hbf, glied?.fbf].filter(hatInhalt).join(' ');
			return i > 0 && glied?.trenner ? `${glied.trenner} ${text}` : text;
		})
		.filter(hatInhalt)
		.join(' ');
}

/** Namen der belegten Felder, jeder Name einmal. */
function belegteNamen(punkt: any, id: AbschnittId): string[] {
	const namen: string[] = [];
	for (const f of abschnitt(id).felder) {
		if (f.abgeleitet || !hatInhalt(leseWert(punkt, f.pfad))) continue;
		if (!namen.includes(f.name)) namen.push(f.name);
	}
	return namen;
}

function zeilenText(anzahl: number, einzahl: string, mehrzahl: string): string {
	return `${anzahl} ${anzahl === 1 ? einzahl : mehrzahl}`;
}

/**
 * Eine kurze Beschreibung dessen, was in diesem Abschnitt steht. Sagt nur, WAS da ist
 * (Zeilen, Namen der belegten Felder), und erfindet keine eigene Schreibweise für die
 * Werte. Leer, wenn der Abschnitt keine Angabe trägt.
 */
export function kurzfassung(punkt: any, id: AbschnittId): string {
	if (!hatAbschnittInhalt(punkt, id)) return '';
	switch (id) {
		case 'substrat': {
			const n = zeilenAnzahl(punkt, 'Substratfolge');
			const rest = belegteNamen(punkt, id).filter(x => x !== 'Substratfolge');
			return [n ? zeilenText(n, 'Zeile', 'Zeilen') : '', ...rest].filter(hatInhalt).join(', ');
		}
		case 'horizonte': {
			const n = zeilenAnzahl(punkt, 'Horizontfolge');
			const rest = belegteNamen(punkt, id).filter(x => x !== 'Horizontfolge');
			return [n ? zeilenText(n, 'Zeile', 'Zeilen') : '', ...rest].filter(hatInhalt).join(', ');
		}
		case 'vegetation': {
			const n = zeilenAnzahl(punkt, 'bodenvegetation.aufnahme.vegetationen');
			const vfg = leseWert(punkt, 'header.vegetationsformengruppe');
			return [n ? zeilenText(n, 'Art', 'Arten') : '', hatInhalt(vfg) ? String(vfg) : ''].filter(hatInhalt).join(', ')
				|| belegteNamen(punkt, id).join(', ');
		}
		case 'bestand': {
			const text = leseWert(punkt, 'extendedHeader.baumarten');
			if (typeof text === 'string' && hatInhalt(text)) return text.trim();
			if (Array.isArray(text) && hatInhalt(text)) return zeilenText(text.filter(hatInhalt).length, 'Probestamm', 'Probestämme');
			return belegteNamen(punkt, id).join(', ');
		}
		case 'bodenform': {
			const bf = bodenformText(punkt);
			const rest = belegteNamen(punkt, id).filter(x => x !== 'Bodenform');
			return [bf, ...rest].filter(hatInhalt).join(', ');
		}
		default:
			return belegteNamen(punkt, id).join(', ');
	}
}

// ---------- Übernehmen ----------

/**
 * Übernimmt die gewählten Abschnitte von `quelle` nach `ziel` und gibt `ziel` zurück.
 *
 * **Ersetzen heißt: der ganze Abschnitt.** Jedes Feld des Abschnitts bekommt den Wert
 * der Quelle. Fehlt er dort, bekommt das Ziel den Leerwert eines neuen Punktes - nicht
 * „nichts", sonst fehlte den Masken ein Objekt, das sie erwarten. Die Werte werden
 * kopiert; Quelle und Ziel teilen sich danach kein Objekt.
 *
 * Zeilen bekommen **eigene Kennungen**: Dieselbe Kennung in zwei Punkten wäre eine
 * Falle für alles, was Zeilen über ihre Kennung wiederfindet (Klärung zweier Fassungen).
 *
 * Abschnitte, die es bei der Punktart des Ziels nicht gibt, werden übergangen.
 */
export function uebernimmAbschnitte<T>(ziel: T, quelle: unknown, ids: readonly AbschnittId[]): T {
	const moeglich = new Set(abschnitteFuer((ziel as any)?.type).map(a => a.id));
	const leer = punktTemplate();
	for (const id of ids) {
		if (!moeglich.has(id)) continue;
		const a = abschnitt(id);
		for (const f of a.felder) {
			const wert = leseWert(quelle, f.pfad);
			setzeWert(ziel, f.pfad, cloneDeep(wert !== undefined ? wert : leseWert(leer, f.pfad)));
		}
		for (const pfad of a.zeilen ?? []) {
			const liste = leseWert(ziel, pfad);
			if (Array.isArray(liste)) setzeWert(ziel, pfad, frischeZeilenKennungen(liste));
		}
	}
	return ziel;
}

/**
 * Roberts Schalter in `DataService.createBohrpunktFrom`: Ohne sie leert das Anlegen die
 * Zeilenlisten wieder. Aus der Wahl im Anlege-Fenster abgeleitet.
 */
export function zeilenBehalten(ids: readonly AbschnittId[]): {
	keepHorizonte: boolean; keepSubstrate: boolean; keepVegetationen: boolean;
} {
	return {
		keepHorizonte: ids.includes('horizonte'),
		keepSubstrate: ids.includes('substrat'),
		keepVegetationen: ids.includes('vegetation'),
	};
}

/** Im Gerätespeicher: die zuletzt im Anlege-Fenster gewählten Abschnitte. */
export const UEBERNAHME_WAHL_KEY = 'PunktUebernahmeAbschnitte';

/** Liest die gemerkte Wahl und wirft dabei alles weg, was es nicht (mehr) gibt. */
export function gemerkteWahl(gespeichert: unknown): AbschnittId[] {
	if (!Array.isArray(gespeichert)) return [];
	const bekannt = new Set<string>(ABSCHNITTE.map(a => a.id));
	return gespeichert.filter((x): x is AbschnittId => typeof x === 'string' && bekannt.has(x));
}
